import React from 'react';
import DateTime from 'react-datetime';

import Calendar from '@gravity-ui/icons/Calendar';
import { Button, Icon, SegmentedRadioGroup, TextInput, TextInputProps } from '@gravity-ui/uikit';
import { moment } from 'client/lib/moment';
import { DateRangeType } from 'client/model/time';
import { calculateRangeType } from 'client/utils/date';
import { isLessThanHalfYear, monitoringDatepickerRanges } from 'client/utils/monitoring-datepicker';

import styles from './AliceAppMonitoringDatePicker.module.scss';

interface DatepickerProps {
    to: moment.Moment;
    from: moment.Moment;
    onChange: (_from: moment.Moment, _to: moment.Moment) => void;
    disabled?: boolean;
}

export const AliceAppMonitoringDatePicker: React.FC<DatepickerProps> = ({ to, from, onChange, disabled }) => {
    const currentRange = calculateRangeType(from, to);

    const renderInput = (props: TextInputProps, openCalendar: () => void) => {
        return (
            <TextInput
                {...props}
                size="l"
                endContent={
                    <Button onClick={openCalendar} view="flat-secondary">
                        <Icon data={Calendar} />
                    </Button>
                }
            />
        );
    };

    const isValidFrom = (current: moment.Moment) => {
        const v = current.clone().startOf('day');
        return v < to && isLessThanHalfYear(v, to);
    };

    const isValidTo = (current: moment.Moment) => {
        const v = current.clone().endOf('day');
        return v > from && isLessThanHalfYear(from, v);
    };

    return (
        <div className={styles.AliceAppMonitoringDatePicker}>
            <SegmentedRadioGroup
                className={styles.AliceAppMonitoringDatePicker__periodRadio}
                disabled={disabled}
                options={Object.entries(monitoringDatepickerRanges).map(([key, { title }]) => ({
                    value: key as DateRangeType,
                    content: title,
                }))}
                onUpdate={(key: DateRangeType) => {
                    if (currentRange === key) {
                        return;
                    }

                    const { getRange } = monitoringDatepickerRanges[key];
                    const { from, to } = getRange();

                    onChange(from, to);
                }}
                value={currentRange}
                size="l"
            />
            <div className={styles.AliceAppMonitoringDatePicker__container}>
                <DateTime
                    {...{ renderInput }}
                    value={from}
                    closeOnSelect
                    isValidDate={isValidFrom}
                    onChange={(value) => {
                        const newFrom = moment(value);

                        if (!isValidFrom(newFrom)) {
                            return;
                        }

                        onChange(newFrom, to);
                    }}
                />
                <div>―</div>
                <DateTime
                    {...{ renderInput }}
                    value={to}
                    closeOnSelect
                    isValidDate={isValidTo}
                    onChange={(value) => {
                        const newTo = moment(value);

                        if (!isValidTo(newTo)) {
                            return;
                        }

                        onChange(from, newTo);
                    }}
                />
            </div>
        </div>
    );
};
